import React from 'react';

import { TabSubrouter } from './TabSubrouter';
import { useUnknownTabRedirect } from './useUnknownTabRedirect';

export interface PermissionTab {
  /** Whether the viewer may see this tab at all. */
  readonly show: boolean;
  /** The URL segment, used for every tab except whichever ends up first. */
  readonly path: string;
  readonly label: string;
  readonly render: () => React.ReactNode;
}

interface PermissionTabSubrouterProps {
  readonly baseUrl: string;
  readonly tabs: readonly PermissionTab[];
}

/**
 * A tab bar whose tabs come and go with the viewer's permissions.
 *
 * The two rules here are easy to get wrong separately, which is why they live in
 * one place:
 *
 * - Whichever tab ends up FIRST carries no `path`, so `TabSubrouter`'s
 *   `value={tab || null}` still matches it on the bare base URL. Give the first
 *   tab an explicit path and the bare URL selects nothing (CarriersSubrouter hit
 *   exactly this).
 * - Which tab is first depends on permissions, so a deep link to a segment that
 *   has since been promoted to first has to be redirected back to the default.
 */
export function PermissionTabSubrouter({
  baseUrl,
  tabs,
}: Readonly<PermissionTabSubrouterProps>) {
  const visible = tabs.filter((tab) => tab.show);

  const subroutes = visible.map((tab, index) =>
    index === 0
      ? { label: tab.label, render: tab.render }
      : { path: tab.path, label: tab.label, render: tab.render }
  );

  const unknownTabRedirect = useUnknownTabRedirect(subroutes, baseUrl);
  if (unknownTabRedirect) {
    return unknownTabRedirect;
  }

  return <TabSubrouter baseUrl={baseUrl} subroutes={subroutes} />;
}

/**
 * Keeps the tab bar mounted while a lazy tab's chunk loads; without it the
 * suspension bubbles to the shell and blanks the whole page, tab bar included,
 * on first visit.
 */
export const withTabSuspense = (node: React.ReactNode) => (
  <React.Suspense fallback={null}>{node}</React.Suspense>
);
